import URLSearchParamsPonyfill from 'core-js-pure/actual/url-search-params';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { AdVendor, PlaybackInitTrackingTypes } from '../types';
import { EntityType } from '../__generated__/api/types';
import { getParameterByName } from '../util/url';
import type { AdobeTrackingObject } from '../modules/Adobe/types';

type TrackingConfig = {
  contentProvider: string | null;
  adobeTraceId: string;
  userId: string;
  initType: PlaybackInitTrackingTypes;
  platform: 'desktop' | 'chromecast' | 'smarttv';
};

export type Metadata = {
  guid?: string;
  title?: string | null;
  entityTitle?: string | null;
  mediaType?: string | null;
  startTime?: number | undefined;
  type?: EntityType | null;
};

export type ProfileTracking = {
  profileId: string;
  profileTraceId: string;
  profileType: string;
  profileInfo: string;
};

type SSAIGoogleDai = {
  integration: 'google-dai';
  adTagParameters: {
    ltd: string;
    ppid?: string;
    cust_params: string;
    sid: string;
  };
  availabilityType: 'live';
  assetKey?: string;
};

type SSAIYospace = {
  integration: 'yospace';
};

type GoogleDaiParams = {
  streamAd?: { segmentation: string; ppid: string };
  customerType?: { providerId: any; productId: any };
  ltd: string;
  sid: string;
};

export type SSAIType = SSAIGoogleDai | SSAIYospace | null;

export function mapAdobeTrackingObject(
  adobe: any,
  config: TrackingConfig,
  isFallbackTracking: boolean,
  id: string,
  profileTracking: ProfileTracking
) {
  const { contentProvider, userId, adobeTraceId, initType, platform } = config;
  const defaultTrackingObject = {
    id,
    title: contentProvider,
    category: null,
    broadcast: contentProvider,
    type: null,
    labels: null,
  };

  const adobeFallback = { ...defaultTrackingObject, ...get(adobe, 'nodes[0].program', adobe) };

  const videoInfo: AdobeTrackingObject = {
    id: defaultTrackingObject.id,
    // We can't send the window.location object directly in v2 - so we send the href instead
    'video.url': window.location.href,
    'video.category': adobeFallback?.category,
    'video.title': adobeFallback?.title,
    'video.streamingtype': adobeFallback?.type,
    'video.id': adobeFallback?.id,
    'video.labels': adobeFallback?.labels,
    'video.initiationtype': initType,
    'video.fallback': isFallbackTracking ? 'true' : 'false',
    'content.platform': platform,
    'content.site': 'tv2play',
    'segment.profile.profileId': profileTracking.profileId,
    'segment.profile.profileTraceId': profileTracking.profileTraceId,
    'segment.profile.profileType': profileTracking.profileType,
    'segment.profile.profileInfo': profileTracking.profileInfo,
    ...(userId && { 'segment.custId': userId, 'segment.traceid': adobeTraceId }),
  };

  return { ...adobeFallback, videoInfo };
}

const mapVODNielsenObject = (nielsen: any) => {
  if (!nielsen?.trackingObject) return nielsen;

  // channelName and pbstarttm is not required for VOD
  const { channelName, pbstarttm, program, ...rest } = JSON.parse(nielsen.trackingObject);
  const mappedTrackingObject = {
    // program is set to NULL for movies, which is not a valid value
    program: program !== 'NULL' ? program : 'unidentified',
    // pbstarttm needs to be an empty string
    pbstarttm: '',
    ...rest,
  };

  return { ...nielsen, trackingObject: JSON.stringify(mappedTrackingObject) };
};

export function mapNielsenObject(
  nielsen: any,
  config: { contentProvider: string | undefined; isLive: boolean }
) {
  const { contentProvider, isLive } = config;
  if (!isLive) return mapVODNielsenObject(nielsen);

  const defaultTrackingObject = {
    type: 'content',
    assetid: 'undefined',
    title: contentProvider,
    program: 'NO VALUE',
    isfullepisode: 'y',
    adloadtype: '2',
    length: '86400',
    airdate: '19700101 00:00:00',
    stationId: '',
    subbrand: '',
    islivestn: 'y',
    pbstarttm: Date.now() / 1000,
  };

  // Get parsed trackingobject from nielsen for content.
  const trackingObject = (nielsen?.nodes || [])
    .map((f: { trackingObject: string }) => JSON.parse(f.trackingObject))
    .filter((t: { type: string }) => t?.type === 'content')[0];

  return {
    // We use merge here because it overwrites undefined values in both objects
    // https://stackoverflow.com/questions/62837977/merge-two-objects-in-javascript-ignoring-undefined-values
    trackingObject: JSON.stringify(merge({}, defaultTrackingObject, trackingObject)),
  };
}

export function mapSSAIForGoogleDai(src: string, params: GoogleDaiParams): SSAIGoogleDai {
  const mappedCustomParams = new URLSearchParamsPonyfill();

  mappedCustomParams.set(
    'segm_params',
    new URLSearchParamsPonyfill(params.streamAd?.segmentation).get('segm_params') || ''
  );
  mappedCustomParams.set('segm_prodid', params.customerType?.providerId || '');
  mappedCustomParams.set('segm_partid', params.customerType?.productId || '');

  const customParamsFromURL = getParameterByName('cust_params', src) || '';
  new URLSearchParamsPonyfill(customParamsFromURL).forEach((v, k) => mappedCustomParams.set(k, v));

  const adTagParameters = {
    ltd: params.ltd,
    ppid: params.streamAd?.ppid || undefined,
    cust_params: mappedCustomParams.toString(),
    sid: params.sid,
  };

  return {
    integration: 'google-dai',
    adTagParameters,
    availabilityType: 'live',
    assetKey: src.match(/\/event\/(.*?)\//i)?.pop(),
  };
}

export function mapSSAI(lar: AdVendor, src: string, parameters?: any): SSAIType {
  if (lar === AdVendor.GOOGLE) {
    return mapSSAIForGoogleDai(src, parameters);
  }

  if (lar === AdVendor.YOSPACE_CS) {
    return {
      integration: 'yospace',
    };
  }

  return null;
}
